import React from 'react';
import { connect } from 'react-redux';
import { createToolbarEvent } from '../../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../../analytics/functions';
import { translate } from '../../../base/i18n/functions';
import AbstractButton from '../../../base/toolbox/components/AbstractButton';
import { setNkfAecEnabled } from '../../actions';
import { isNkfAecEnabled, isNkfAecReady, isNkfAecSettling, isNkfAecStalled } from '../../functions';
import IconNkfAecWave from './IconNkfAecWave';
/**
 * Toolbar button that toggles NKF-AEC echo cancellation. The waveform glyph is
 * applied by the DeepAI theme CSS (matching the demo's エコーキャンセル button).
 */
class NkfAecButton extends AbstractButton {
    accessibilityLabel = 'toolbar.accessibilityEchoCancel';
    icon = IconNkfAecWave;
    label = 'toolbar.echoCancel';
    toggledLabel = 'toolbar.echoCancelOff';
    tooltip = 'toolbar.echoCancel';
    /**
     * Toggles echo cancellation.
     *
     * @override
     * @protected
     * @returns {void}
     */
    _handleClick() {
        const { _enabled, dispatch } = this.props;
        sendAnalytics(createToolbarEvent('nkf.aec', { enable: !_enabled }));
        dispatch(setNkfAecEnabled(!_enabled));
    }
    /**
     * Toggled (ON) when echo cancellation is enabled.
     *
     * @override
     * @protected
     * @returns {boolean}
     */
    _isToggled() {
        return this.props._enabled;
    }
    /**
     * While echo cancellation is ON but has not reached a working state yet
     * (right after joining, before the echo delay is known), say so instead of
     * implying it is already protecting the call.
     *
     * Display only — the audio path is unchanged.
     *
     * @override
     * @protected
     * @returns {string}
     */
    _getLabel() {
        const { _enabled, _ready } = this.props;
        if (_enabled && this.props._stalled) {
            return 'toolbar.echoCancelFault';
        }
        if (_enabled && this.props._settling) {
            return 'toolbar.echoCancelSettling';
        }
        if (_enabled && !_ready) {
            return 'toolbar.echoCancelPreparing';
        }
        return _enabled ? this.toggledLabel : this.label;
    }
    /**
     * Same wording on hover — the main toolbar shows only the icon, so the
     * tooltip is where the label actually reaches the user there.
     *
     * @override
     * @protected
     * @returns {string}
     */
    _getTooltip() {
        return this._getLabel();
    }
    /**
     * Badge on the button itself while echo cancellation is ON but has not
     * reached a working state yet (right after joining, before the echo delay
     * is known).
     *
     * The main toolbar renders icon-only — `label` is shown only inside the
     * overflow menu — so `elementAfter` is the one hook that puts a visible
     * mark on the bottom-bar button. Same approach as the slide button's
     * attention badge.
     *
     * Display only: the audio path is unchanged.
     *
     * @override
     * @protected
     * @returns {ReactElement|null}
     */
    _getElementAfter() {
        const { _enabled, _ready, _settling, _stalled } = this.props;
        if (!_enabled || (_ready && !_stalled)) {
            return null;
        }
        // Three distinct states, because they mean different things to the user:
        //   fault     — nothing is being processed, and it will not fix itself
        //   settling  — silent, but the delay is found and this ends on its own
        //   preparing — silent, and we still do not know where the echo is
        const cls = _stalled ? 'nkf-aec-fault-badge'
            : _settling ? 'nkf-aec-settling-badge'
                : 'nkf-aec-preparing-badge';
        return (React.createElement("span", { "aria-hidden": true, className: cls }));
    }
}
/**
 * Maps part of the redux state to the component's props.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {Object}
 */
function mapStateToProps(state) {
    return {
        _enabled: isNkfAecEnabled(state),
        _ready: isNkfAecReady(state),
        _settling: isNkfAecSettling(state),
        _stalled: isNkfAecStalled(state)
    };
}
export default translate(connect(mapStateToProps)(NkfAecButton));
